// src/pages/CookiePolicyPage.tsx
import React from "react";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiDatabase } from "react-icons/fi";
import { usePageTitle } from "../hooks/usePageTitle";

const CookiePolicyPage: React.FC = () => {
	usePageTitle("Cookieポリシー");

	return (
		<div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
			<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
				{/* Header */}
				<div className="mb-8">
					<Link
						to="/"
						className="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-4"
					>
						<FiArrowLeft className="w-4 h-4 mr-1" />
						トップページに戻る
					</Link>
					<div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8">
						<div className="flex items-center gap-4 mb-4">
							<div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full">
								<FiDatabase className="w-6 h-6 text-white" />
							</div>
							<div>
								<h1 className="text-3xl font-bold text-gray-900 dark:text-white">
									Cookieポリシー
								</h1>
								<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
									最終更新日: 2026年3月22日
								</p>
							</div>
						</div>
					</div>
				</div>

				{/* Content */}
				<div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8">
					<div className="prose prose-gray dark:prose-invert max-w-none">
						<section className="mb-8">
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								合同会社blackadder（以下「当社」といいます）は、イベントチケット販売プラットフォーム「b+
								ticket」（以下「本サービス」といいます）において、Cookie及びこれに類する技術を使用しています。
								本ポリシーでは、Cookieの使用目的や管理方法についてご説明いたします。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第1条（Cookieとは）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								Cookieとは、ウェブサイトを閲覧する際に、ウェブサーバーからお客様のブラウザに送信され、お客様のコンピュータまたはモバイル端末に保存される小さなテキストファイルです。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								Cookieは、ウェブサイトがお客様のブラウザを認識し、閲覧履歴や設定情報を記憶することで、より快適なサービス提供を可能にします。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第2条（使用するCookieの種類）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
								本サービスでは、以下の種類のCookieを使用しています。
							</p>

							<div className="mb-4">
								<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
									1. 必須Cookie
								</h3>
								<p className="text-gray-700 dark:text-gray-300 leading-relaxed ml-4 mb-2">
									本サービスの基本的な機能を提供するために必要なCookieです。これらを無効にすると、サービスが正常に動作しない場合があります。
								</p>
								<ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 ml-8">
									<li>ログイン状態の維持</li>
									<li>セキュリティトークンの管理</li>
									<li>
										ショッピングカート・購入プロセスの維持
									</li>
								</ul>
							</div>

							<div className="mb-4">
								<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
									2. 機能Cookie
								</h3>
								<p className="text-gray-700 dark:text-gray-300 leading-relaxed ml-4 mb-2">
									お客様の設定や選択を記憶し、より便利にサービスをご利用いただくためのCookieです。
								</p>
								<ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 ml-8">
									<li>テーマ設定（ライトモード/ダークモード）の記憶</li>
									<li>言語設定の記憶</li>
									<li>表示設定の記憶</li>
								</ul>
							</div>

							<div className="mb-4">
								<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
									3. 分析Cookie
								</h3>
								<p className="text-gray-700 dark:text-gray-300 leading-relaxed ml-4 mb-2">
									サービスの利用状況を分析し、改善に役立てるためのCookieです。
								</p>
								<ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 ml-8">
									<li>ページの閲覧回数・滞在時間の計測</li>
									<li>利用者の行動パターンの分析</li>
									<li>
										エラーの検出とサービス品質の向上
									</li>
								</ul>
							</div>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第3条（第三者によるCookieの使用）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								本サービスでは、以下の第三者サービスによるCookieが設定される場合があります。
							</p>
							<div className="overflow-x-auto">
								<table className="w-full border-collapse border border-gray-200 dark:border-gray-700">
									<thead>
										<tr className="bg-gray-50 dark:bg-gray-700/50">
											<th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold border border-gray-200 dark:border-gray-700">
												サービス名
											</th>
											<th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold border border-gray-200 dark:border-gray-700">
												目的
											</th>
											<th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold border border-gray-200 dark:border-gray-700">
												種類
											</th>
										</tr>
									</thead>
									<tbody>
										<tr>
											<td className="py-3 px-4 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
												Stripe
											</td>
											<td className="py-3 px-4 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
												決済処理・不正防止
											</td>
											<td className="py-3 px-4 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
												必須
											</td>
										</tr>
										<tr>
											<td className="py-3 px-4 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
												Firebase Authentication
											</td>
											<td className="py-3 px-4 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
												ユーザー認証・ログイン管理
											</td>
											<td className="py-3 px-4 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
												必須
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第4条（Cookieの管理方法）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								お客様は、ブラウザの設定を変更することにより、Cookieの受け入れを拒否したり、既に保存されているCookieを削除することができます。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								主要なブラウザにおけるCookieの管理方法は以下のとおりです。
							</p>
							<ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
								<li>
									<span className="font-semibold">
										Google Chrome:
									</span>{" "}
									設定 → プライバシーとセキュリティ →
									Cookieと他のサイトデータ
								</li>
								<li>
									<span className="font-semibold">
										Safari:
									</span>{" "}
									環境設定 → プライバシー →
									Cookieとウェブサイトのデータ
								</li>
								<li>
									<span className="font-semibold">
										Microsoft Edge:
									</span>{" "}
									設定 → Cookieとサイトのアクセス許可 →
									Cookieとサイトデータの管理と削除
								</li>
								<li>
									<span className="font-semibold">
										Mozilla Firefox:
									</span>{" "}
									設定 → プライバシーとセキュリティ →
									Cookieとサイトデータ
								</li>
							</ul>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
								なお、Cookieを無効にした場合、本サービスの一部機能がご利用いただけなくなる場合がありますので、あらかじめご了承ください。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第5条（プライバシーポリシーとの関係）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								Cookieを通じて収集される情報の取扱いについては、当社の
								<Link
									to="/privacy-policy"
									className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
								>
									プライバシーポリシー
								</Link>
								に準じます。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第6条（ポリシーの変更）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								当社は、必要に応じて本ポリシーの内容を変更することがあります。変更後のポリシーは、本ページに掲載した時点から効力を生じるものとします。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第7条（お問い合わせ）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								本ポリシーに関するお問い合わせは、以下までご連絡ください。
							</p>
							<div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
								<p className="text-gray-700 dark:text-gray-300">
									合同会社blackadder
								</p>
								<p className="text-gray-700 dark:text-gray-300">
									メール:{" "}
									<a
										href="mailto:info@blackadder.co.jp"
										className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
									>
										info@blackadder.co.jp
									</a>
								</p>
							</div>
						</section>
					</div>
				</div>
			</div>
		</div>
	);
};

export default CookiePolicyPage;
